import React from "react";
import { useNavigate } from "react-router-dom";

const THEMES = {
  light: {
    bg: "#ffffff",
    bgSub: "#f7f7f8",
    surface: "#f0f0f2",
    surfaceHover: "#e8e8eb",
    border: "#e3e3e6",
    borderStrong: "#c5c5ca",
    text: "#111111",
    textSub: "#5f5f6b",
    textMuted: "#a3a3b0",
    accent: "#111111",
    accentText: "#ffffff",
    success: "#188038",
    shadow: "0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.05)",
    shadowMd: "0 10px 30px rgba(0,0,0,0.06)",
    shadowLg: "0 20px 50px rgba(0,0,0,0.08)",
  },
  dark: {
    bg: "#212121",
    bgSub: "#2a2a2a",
    surface: "#303030",
    surfaceHover: "#3a3a3a",
    border: "#3a3a3a",
    borderStrong: "#4e4e4e",
    text: "#ececec",
    textSub: "#9e9e9e",
    textMuted: "#616161",
    accent: "#ececec",
    accentText: "#111111",
    success: "#81c995",
    shadow: "0 1px 3px rgba(0,0,0,0.3)",
    shadowMd: "0 8px 24px rgba(0,0,0,0.28)",
    shadowLg: "0 18px 44px rgba(0,0,0,0.35)",
  },
};

export default function LandingPage() {
  const navigate = useNavigate();

  const darkMode =
    typeof window !== "undefined" &&
    window.localStorage.getItem("darkMode") === "true";

  const T = darkMode ? THEMES.dark : THEMES.light;

  const features = [
    {
      title: "Natural Conversations",
      desc: "Chat with Omrali in a simple, human-like way for answers, ideas, and assistance.",
      icon: "chat",
    },
    {
      title: "Fast Responses",
      desc: "Get instant replies for questions, brainstorming, writing help, and everyday tasks.",
      icon: "bolt",
    },
    {
      title: "Smart Assistance",
      desc: "Use Omrali to think, write, learn, explore ideas, and stay productive.",
      icon: "psychology",
    },
    {
      title: "Always Available",
      desc: "Your AI assistant is ready anytime you need help, inspiration, or quick answers.",
      icon: "schedule",
    },
    {
      title: "Simple Experience",
      desc: "Clean, distraction-free chat experience designed to feel fast and easy to use.",
      icon: "auto_awesome",
    },
    {
      title: "Built to Grow",
      desc: "Omrali is starting as chat-first AI and evolving into something much more powerful.",
      icon: "rocket_launch",
    },
  ];

  const stats = [
    { value: "AI", label: "Chat-first experience" },
    { value: "24/7", label: "Always available" },
    { value: "Fast", label: "Instant responses" },
    { value: "Smart", label: "Built to assist" },
  ];

  const useCases = [
    {
      number: "01",
      icon: "edit_note",
      text: "Writing, rewriting, and idea generation",
    },
    {
      number: "02",
      icon: "school",
      text: "Learning, explanations, and asking questions",
    },
    {
      number: "03",
      icon: "lightbulb",
      text: "Brainstorming and creative thinking",
    },
    {
      number: "04",
      icon: "code",
      text: "Developer help and technical assistance",
    },
    {
      number: "05",
      icon: "workspaces",
      text: "Everyday productivity and quick support",
    },
  ];

  const whyItems = [
    {
      icon: "check_circle",
      text: "Clean chat-first product experience with a modern AI feel",
    },
    {
      icon: "check_circle",
      text: "Useful for everyday questions, productivity, and creative work",
    },
    {
      icon: "check_circle",
      text: "Built with long-term vision beyond just basic chatbot interactions",
    },
  ];

  return (
    <div
      style={{
        minHeight: "100vh",
        background: T.bg,
        color: T.text,
        fontFamily: "'Google Sans Text', 'Google Sans', system-ui, sans-serif",
      }}
    >
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        body { background: ${T.bg}; }

        a {
          text-decoration: none;
          color: inherit;
        }

        .material-symbols-rounded {
          font-variation-settings:
            'FILL' 0,
            'wght' 500,
            'GRAD' 0,
            'opsz' 24;
          line-height: 1;
          user-select: none;
        }

        .container {
          max-width: 1180px;
          margin: 0 auto;
          padding: 0 22px;
        }

        .nav {
          position: sticky;
          top: 0;
          z-index: 100;
          background: ${darkMode ? "rgba(33,33,33,0.88)" : "rgba(255,255,255,0.88)"};
          backdrop-filter: blur(16px);
          border-bottom: 1px solid ${T.border};
        }

        .nav-inner {
          min-height: 64px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 18px;
        }

        .brand {
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
        }

        .brand-icon {
          width: 34px;
          height: 34px;
          border-radius: 10px;
          background: ${T.accent};
          color: ${T.accentText};
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: ${T.shadow};
        }

        .brand-icon .material-symbols-rounded {
          font-size: 18px;
        }

        .brand-text {
          font-size: 22px;
          font-weight: 700;
          letter-spacing: -0.02em;
          color: ${T.text};
        }

        .brand-text span {
          color: ${T.textSub};
          font-weight: 400;
        }

        .nav-links {
          display: flex;
          align-items: center;
          gap: 22px;
        }

        .nav-links a {
          font-size: 14px;
          font-weight: 500;
          color: ${T.textSub};
          transition: color 0.15s ease;
        }

        .nav-links a:hover {
          color: ${T.text};
        }

        .nav-actions {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .btn-primary {
          border: none;
          background: ${T.accent};
          color: ${T.accentText};
          padding: 11px 16px;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: opacity 0.15s ease, transform 0.15s ease;
        }

        .btn-primary:hover {
          opacity: .9;
          transform: translateY(-1px);
        }

        .btn-ghost {
          border: 1px solid ${T.border};
          background: ${T.bg};
          color: ${T.text};
          padding: 11px 16px;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.15s ease, border-color 0.15s ease;
        }

        .btn-ghost:hover {
          background: ${T.surface};
          border-color: ${T.borderStrong};
        }

        .hero {
          padding: 72px 0 54px;
        }

        .hero-grid {
          display: grid;
          grid-template-columns: 1.05fr 0.95fr;
          gap: 24px;
          align-items: center;
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          border-radius: 999px;
          border: 1px solid ${T.border};
          background: ${T.bgSub};
          color: ${T.textSub};
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          margin-bottom: 18px;
        }

        .hero-dot {
          width: 8px;
          height: 8px;
          border-radius: 999px;
          background: ${T.accent};
          display: inline-block;
        }

        .hero-title {
          font-size: clamp(38px, 6vw, 68px);
          line-height: 1.02;
          font-weight: 800;
          letter-spacing: -0.05em;
          color: ${T.text};
          margin-bottom: 18px;
          max-width: 760px;
        }

        .hero-title .subtle {
          color: ${T.textSub};
          font-weight: 600;
        }

        .hero-text {
          font-size: 15.5px;
          line-height: 1.9;
          color: ${T.textSub};
          max-width: 640px;
          margin-bottom: 26px;
        }

        .hero-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          margin-bottom: 30px;
        }

        .hero-stats {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 12px;
        }

        .stat {
          background: ${T.bgSub};
          border: 1px solid ${T.border};
          border-radius: 16px;
          padding: 16px;
          box-shadow: ${T.shadow};
        }

        .stat-value {
          font-size: 20px;
          font-weight: 700;
          color: ${T.text};
          margin-bottom: 4px;
        }

        .stat-label {
          font-size: 12.5px;
          line-height: 1.6;
          color: ${T.textSub};
        }

        .hero-card {
          background: ${T.bgSub};
          border: 1px solid ${T.border};
          border-radius: 24px;
          padding: 20px;
          box-shadow: ${T.shadowLg};
        }

        .mini-label {
          font-size: 11px;
          font-weight: 700;
          color: ${T.textSub};
          text-transform: uppercase;
          letter-spacing: 0.08em;
          margin-bottom: 12px;
        }

        .ai-panel {
          background: ${T.bg};
          border: 1px solid ${T.border};
          border-radius: 18px;
          padding: 18px;
          margin-bottom: 14px;
        }

        .ai-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 14px;
        }

        .ai-left {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .ai-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background: ${T.success};
        }

        .ai-name {
          font-weight: 700;
          font-size: 14px;
          color: ${T.text};
        }

        .ai-status {
          font-size: 12px;
          color: ${T.textSub};
          font-weight: 600;
        }

        .chat-bubble {
          background: ${T.bgSub};
          border: 1px solid ${T.border};
          border-radius: 16px;
          padding: 14px;
          font-size: 14px;
          line-height: 1.75;
          color: ${T.text};
          margin-bottom: 12px;
        }

        .chat-bubble.user {
          background: ${T.accent};
          color: ${T.accentText};
          border-color: ${T.accent};
        }

        .pill-row {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-top: 12px;
        }

        .pill {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: ${T.surface};
          border: 1px solid ${T.border};
          color: ${T.textSub};
          font-size: 12px;
          font-weight: 600;
          padding: 8px 12px;
          border-radius: 999px;
        }

        .pill .material-symbols-rounded {
          font-size: 16px;
        }

        .section {
          padding: 76px 0;
        }

        .section-head {
          text-align: center;
          max-width: 760px;
          margin: 0 auto 38px;
        }

        .section-kicker {
          font-size: 11px;
          font-weight: 700;
          color: ${T.textSub};
          text-transform: uppercase;
          letter-spacing: 0.08em;
          margin-bottom: 10px;
        }

        .section-title {
          font-size: clamp(28px, 4vw, 48px);
          line-height: 1.08;
          font-weight: 800;
          letter-spacing: -0.04em;
          color: ${T.text};
          margin-bottom: 14px;
        }

        .section-sub {
          font-size: 15px;
          line-height: 1.85;
          color: ${T.textSub};
        }

        .features-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 18px;
        }

        .feature-card {
          background: ${T.bgSub};
          border: 1px solid ${T.border};
          border-radius: 20px;
          padding: 22px;
          box-shadow: ${T.shadow};
          transition: transform 0.15s ease, border-color 0.15s ease, background 0.15s ease;
        }

        .feature-card:hover {
          transform: translateY(-3px);
          border-color: ${T.borderStrong};
          background: ${T.surface};
        }

        .feature-icon {
          width: 46px;
          height: 46px;
          border-radius: 14px;
          background: ${T.surface};
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 16px;
        }

        .feature-icon .material-symbols-rounded {
          font-size: 24px;
          color: ${T.text};
        }

        .feature-card h3 {
          font-size: 18px;
          font-weight: 700;
          color: ${T.text};
          margin-bottom: 10px;
        }

        .feature-card p {
          font-size: 14px;
          line-height: 1.8;
          color: ${T.textSub};
        }

        .why-wrap {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 18px;
        }

        .why-card {
          background: ${T.bgSub};
          border: 1px solid ${T.border};
          border-radius: 22px;
          padding: 24px;
          box-shadow: ${T.shadow};
        }

        .why-card h3 {
          font-size: 28px;
          font-weight: 750;
          letter-spacing: -0.03em;
          color: ${T.text};
          margin-bottom: 14px;
        }

        .why-card p {
          font-size: 14.5px;
          line-height: 1.9;
          color: ${T.textSub};
          margin-bottom: 18px;
        }

        .why-list {
          display: grid;
          gap: 12px;
        }

        .why-item {
          display: flex;
          gap: 12px;
          align-items: flex-start;
          background: ${T.bg};
          border: 1px solid ${T.border};
          border-radius: 16px;
          padding: 14px;
        }

        .why-check {
          width: 32px;
          height: 32px;
          border-radius: 999px;
          background: ${T.surface};
          color: ${T.text};
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .why-check .material-symbols-rounded {
          font-size: 18px;
        }

        .why-number {
          font-size: 12px;
          font-weight: 700;
        }

        .why-item span:last-child {
          font-size: 14px;
          line-height: 1.7;
          color: ${T.text};
          font-weight: 500;
        }

        .cta {
          padding: 0 0 90px;
        }

        .cta-box {
          background: ${T.bgSub};
          border: 1px solid ${T.border};
          border-radius: 28px;
          padding: 42px 28px;
          text-align: center;
          box-shadow: ${T.shadowLg};
        }

        .cta-box h2 {
          font-size: clamp(28px, 4vw, 46px);
          line-height: 1.08;
          font-weight: 800;
          letter-spacing: -0.04em;
          color: ${T.text};
          margin-bottom: 14px;
        }

        .cta-box p {
          max-width: 760px;
          margin: 0 auto 22px;
          font-size: 15px;
          line-height: 1.85;
          color: ${T.textSub};
        }

        .cta-actions {
          display: flex;
          justify-content: center;
          flex-wrap: wrap;
          gap: 12px;
        }

        .footer {
          padding: 0 0 26px;
        }

        .footer-box {
          background: ${T.bgSub};
          border: 1px solid ${T.border};
          border-radius: 22px;
          box-shadow: ${T.shadow};
        }

        .footer-inner {
          padding: 28px 24px 20px;
        }

        .footer-top {
          display: grid;
          grid-template-columns: 1.8fr 1fr 1fr 1fr 1fr;
          gap: 28px;
          margin-bottom: 22px;
        }

        .footer-logo {
          font-size: 22px;
          font-weight: 800;
          color: ${T.text};
          margin-bottom: 10px;
        }

        .footer-logo span {
          color: ${T.textSub};
          font-weight: 400;
        }

        .footer-brand p {
          font-size: 13px;
          line-height: 1.75;
          color: ${T.textSub};
        }

        .footer-col h4 {
          font-size: 13px;
          font-weight: 700;
          color: ${T.text};
          margin-bottom: 14px;
        }

        .footer-col a {
          display: block;
          font-size: 13px;
          color: ${T.textSub};
          margin-bottom: 10px;
          transition: color 0.15s ease;
        }

        .footer-col a:hover {
          color: ${T.text};
        }

        .footer-divider {
          height: 1px;
          background: ${T.border};
          margin-bottom: 14px;
        }

        .footer-bottom span {
          font-size: 12px;
          color: ${T.textMuted};
        }

        @media (max-width: 1024px) {
          .hero-grid,
          .why-wrap {
            grid-template-columns: 1fr;
          }

          .features-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .footer-top {
            grid-template-columns: 1fr 1fr;
          }
        }

        @media (max-width: 768px) {
          .nav-links {
            display: none;
          }

          .hero {
            padding: 52px 0 40px;
          }

          .hero-stats {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .features-grid {
            grid-template-columns: 1fr;
          }

          .section {
            padding: 62px 0;
          }

          .footer-top {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 480px) {
          .container {
            padding: 0 16px;
          }

          .nav-inner {
            flex-wrap: wrap;
            padding: 12px 0;
          }

          .nav-actions {
            width: 100%;
            justify-content: flex-start;
          }

          .hero-stats {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      {/* NAV */}
      <nav className="nav">
        <div className="container nav-inner">
          <div className="brand" onClick={() => navigate("/")}>
            <div className="brand-text">
              Omrali <span>AI</span>
            </div>
          </div>

          <div className="nav-links">
            <a href="#features">Features</a>
            <a href="#why">Why Omrali</a>
            <a href="https://chat.omrali.com/about">About</a>
            <a href="https://chat.omrali.com/contact">Contact</a>
          </div>

          <div className="nav-actions">
            <button
              className="btn-ghost"
              onClick={() =>
                window.open(
                  "https://chat.omrali.com",
                  "_blank",
                  "noopener,noreferrer"
                )
              }
            >
              Use Omrali
            </button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="hero">
        <div className="container hero-grid">
          <div>
            <div className="hero-badge">
              <span className="hero-dot" />
              Next-generation conversational AI
            </div>

            <h1 className="hero-title">
              Chat with AI for{" "}
              <span className="subtle">ideas, answers, and everyday help.</span>
            </h1>

            <p className="hero-text">
              Omrali AI is a fast, clean, chat-based assistant designed to help
              you think, write, learn, explore ideas, and get useful answers in
              seconds.
            </p>

            <div className="hero-actions">
              <button
                className="btn-primary"
                onClick={() =>
                  window.open(
                    "https://chat.omrali.com",
                    "_blank",
                    "noopener,noreferrer"
                  )
                }
              >
                Use Omrali AI
              </button>

              <button
                className="btn-ghost"
                onClick={() =>
                  window.open(
                    "https://chat.omrali.com/about",
                    "_blank",
                    "noopener,noreferrer"
                  )
                }
              >
                Learn More
              </button>
            </div>

            <div className="hero-stats">
              {stats.map((item, i) => (
                <div className="stat" key={i}>
                  <div className="stat-value">{item.value}</div>
                  <div className="stat-label">{item.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="hero-card">
            <div className="mini-label">Live chat preview</div>

            <div className="ai-panel">
              <div className="ai-header">
                <div className="ai-left">
                  <div className="ai-dot" />
                  <div className="ai-name">Omrali Assistant</div>
                </div>
                <div className="ai-status">Active</div>
              </div>

              <div className="chat-bubble user">
                Help me write a better landing page headline for my startup.
              </div>

              <div className="chat-bubble">
                Absolutely — here are a few stronger headline ideas depending on
                whether you want it to feel more premium, bold, or
                product-focused.
              </div>

              <div className="pill-row">
                <div className="pill">
                  <span className="material-symbols-rounded">edit_note</span>
                  Writing
                </div>
                <div className="pill">
                  <span className="material-symbols-rounded">lightbulb</span>
                  Ideas
                </div>
                <div className="pill">
                  <span className="material-symbols-rounded">chat</span>
                  Chat AI
                </div>
                <div className="pill">
                  <span className="material-symbols-rounded">psychology</span>
                  Smart Help
                </div>
              </div>
            </div>

            <div className="ai-panel" style={{ marginBottom: 0 }}>
              <div className="mini-label" style={{ marginBottom: 10 }}>
                Perfect for
              </div>
              <div className="pill-row">
                <div className="pill">
                  <span className="material-symbols-rounded">school</span>
                  Students
                </div>
                <div className="pill">
                  <span className="material-symbols-rounded">code</span>
                  Developers
                </div>
                <div className="pill">
                  <span className="material-symbols-rounded">work</span>
                  Professionals
                </div>
                <div className="pill">
                  <span className="material-symbols-rounded">draw</span>
                  Creators
                </div>
                <div className="pill">
                  <span className="material-symbols-rounded">explore</span>
                  Curious minds
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="section" id="features">
        <div className="container">
          <div className="section-head">
            <div className="section-kicker">Platform features</div>
            <h2 className="section-title">A cleaner way to use AI every day</h2>
            <p className="section-sub">
              Omrali is built to make AI feel useful, simple, and natural —
              whether you’re asking questions, writing, learning, or just
              exploring ideas.
            </p>
          </div>

          <div className="features-grid">
            {features.map((feature, i) => (
              <div className="feature-card" key={i}>
                <div className="feature-icon">
                  <span className="material-symbols-rounded">
                    {feature.icon}
                  </span>
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="section" id="why" style={{ paddingTop: 0 }}>
        <div className="container">
          <div className="why-wrap">
            <div className="why-card">
              <div className="section-kicker">Why choose Omrali</div>
              <h3>More than just a basic chatbot.</h3>
              <p>
                Omrali is designed to feel useful from day one — fast enough for
                everyday use, flexible enough for different needs, and built
                with bigger ambitions over time.
              </p>

              <div className="why-list">
                {whyItems.map((item, i) => (
                  <div className="why-item" key={i}>
                    <div className="why-check">
                      <span className="material-symbols-rounded">
                        {item.icon}
                      </span>
                    </div>
                    <span>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="why-card">
              <div className="section-kicker">Use cases</div>
              <h3>Where Omrali can help</h3>

              <div className="why-list">
                {useCases.map((item, i) => (
                  <div className="why-item" key={i}>
                    <div className="why-check">
                      <span className="why-number">{item.number}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span
                        className="material-symbols-rounded"
                        style={{ fontSize: 18, color: T.textSub }}
                      >
                        {item.icon}
                      </span>
                      <span>{item.text}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta">
        <div className="container">
          <div className="cta-box">
            <h2>Be early to what Omrali is building</h2>
            <p>
              Join the waitlist for early access, beta invites, product updates,
              and launch announcements as Omrali evolves into a powerful AI
              experience.
            </p>

            <div className="cta-actions">
              <button
                className="btn-primary"
                onClick={() =>
                  window.open(
                    "https://chat.omrali.com/waitlist",
                    "_blank",
                    "noopener,noreferrer"
                  )
                }
              >
                Join Waitlist
              </button>

              <button
                className="btn-ghost"
                onClick={() =>
                  window.open(
                    "https://chat.omrali.com/contact",
                    "_blank",
                    "noopener,noreferrer"
                  )
                }
              >
                Contact Us
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="footer">
        <div className="container">
          <div className="footer-box">
            <div className="footer-inner">
              <div className="footer-top">
                <div className="footer-brand">
                  <div className="footer-logo">
                    Omrali <span>AI</span>
                  </div>
                  <p>
                    Fast, modern conversational AI designed for ideas, answers,
                    and everyday use.
                  </p>
                </div>

                <div className="footer-col">
                  <h4>Product</h4>
                  <a href="https://chat.omrali.com">Home</a>
                  <a href="https://chat.omrali.com/waitlist">Beta</a>
                  <a href="/">API docs (coming soon)</a>
                </div>

                <div className="footer-col">
                  <h4>Company</h4>
                  <a href="https://chat.omrali.com/about">About us</a>
                  <a href="https://chat.omrali.com/contact">Contact</a>
                  <a href="https://chat.omrali.com/waitlist">Waitlist</a>
                </div>

                <div className="footer-col">
                  <h4>Legal</h4>
                  <a href="https://chat.omrali.com/privacy">Privacy policy</a>
                  <a href="https://chat.omrali.com/terms">Terms of service</a>
                </div>

                <div className="footer-col">
                  <h4>Social</h4>
                  <a href="https://www.instagram.com/omrali.ai/">Instagram</a>
                  <a href="https://twitter.com/omrali_ai">Twitter</a>
                </div>
              </div>

              <div className="footer-divider" />

              <div className="footer-bottom">
                <span>© 2026 Omrali AI. All rights reserved.</span>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}